// src/client/renderer/vfx/tile/tile-bounce.ts
//
// Plan 16, Task 3.1 — Tile bounce pool (paired side-effect of LandingEvent).
//
// Operates on the actual tile-marker mesh (NOT a pool emitter mesh).
// The pool tracks active markers via Map<emitter, {originalScale, timeline}>.
//
// Animation timeline (gsap, manual-ticker via GsapRegistry):
//   scale.y: 1.0 → 0.6 → 1.2 → 1.0 (squash-stretch) over 400ms.
//   Completion callback at 0.4s (absolute) restores the original scale and marks finished.
//
// Pre-bounce scale MUST be captured BEFORE animating and restored on completion
// so bouncing the same tile twice in a row does not drift.
//
// acquire() without a tileMarker in args returns null (defensive — landing-fx still fires).
//
// Forward-defense F4: pool.dispose() MUST iterate active emitters, kill all active
// timelines, and restore all original scales WITHOUT expecting prior release() calls.

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { GsapRegistry } from '../gsap-registry';
import type { GameEvent } from '../../../../shared/game/types';

// ─────────────────────────────────────────────────────────────────────────────
// Animation constants
// ─────────────────────────────────────────────────────────────────────────────

const SQUASH_Y = 0.6; // minimum scale.y during squash
const STRETCH_Y = 1.2; // maximum scale.y during stretch
const LIFETIME = 0.4; // seconds (400ms total bounce duration)

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface BounceState {
  /** Cloned pre-bounce scale so we can restore it identically. */
  originalScale: THREE.Vector3;
  handle: ReturnType<GsapRegistry['create']>;
  finished: boolean;
  restored: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface TileBouncePoolConfig {
  readonly registry: GsapRegistry;
}

export function createTileBouncePool({ registry }: TileBouncePoolConfig): TileVfxPool {
  // Active emitter state: each "emitter" is the tileMarker Object3D itself.
  const emitterStates = new Map<THREE.Object3D, BounceState>();

  let disposed = false;

  // ── internal restore helper ────────────────────────────────────────────────

  function restoreScale(emitter: THREE.Object3D, state: BounceState): void {
    if (state.restored) return;
    state.restored = true;
    emitter.scale.copy(state.originalScale);
  }

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // tile-bounce is a no-op if no tileMarker is provided
    const tileMarker = args.tileMarker;
    if (!tileMarker) return null;

    // Capture pre-bounce scale BEFORE animating (clone to avoid aliasing)
    const originalScale = tileMarker.scale.clone();

    // Build gsap timeline via registry
    const handle = registry.create('tile_bounce');
    const tl = handle.timeline;

    const state: BounceState = {
      originalScale,
      handle,
      finished: false,
      restored: false,
    };

    // Squash-stretch animation on scale.y:
    //   0ms:   1.0 (original, captured above)
    //   ~133ms: 0.6 (squash — impact)
    //   ~266ms: 1.2 (stretch — rebound)
    //   400ms: 1.0 (return to original)
    //
    // Implemented as three sequential tweens within the 400ms window.
    const segDuration = LIFETIME / 3; // ~133ms each

    tl.to(
      tileMarker.scale,
      {
        y: SQUASH_Y,
        duration: segDuration,
        ease: 'power2.in',
      },
      0,
    );

    tl.to(
      tileMarker.scale,
      {
        y: STRETCH_Y,
        duration: segDuration,
        ease: 'power2.out',
      },
      segDuration,
    );

    tl.to(
      tileMarker.scale,
      {
        y: originalScale.y,
        duration: segDuration,
        ease: 'power2.inOut',
      },
      segDuration * 2,
    );

    // Completion callback at absolute 0.4s — use tl.call(fn, undefined, abs).
    // Do NOT use eventCallback('onComplete') — see gsap-registry.ts VIRTUAL_DURATION note.
    tl.call(
      () => {
        state.finished = true;
        restoreScale(tileMarker, state);
      },
      undefined,
      LIFETIME,
    );

    emitterStates.set(tileMarker, state);
    return tileMarker;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    if (!state) return true; // not tracked — treat as finished
    return state.finished;
  }

  // ── release ────────────────────────────────────────────────────────────────
  // Idempotent — safe to call twice. Second call is a no-op.

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return; // already released or unknown — safe no-op

    restoreScale(emitter, state);
    state.handle.dispose();
    emitterStates.delete(emitter);
  }

  // ── dispose ────────────────────────────────────────────────────────────────
  // Forward-defense F4: iterates active emitters, kills all timelines,
  // restores all original scales WITHOUT expecting prior release() calls.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const [emitter, state] of emitterStates) {
      restoreScale(emitter, state);
      state.handle.dispose();
    }
    emitterStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a `LandingEvent` to tile-bounce spawn args.
 *
 * Returns null for:
 *   - any non-landing event
 *   - landing events where getTileMarker is not provided
 *   - landing events where getTileMarker(event.nodeId) returns undefined
 *
 * The tileMarker is the actual tile-mesh Object3D that will be animated in-place.
 * When null is returned, landing-fx particles still fire — tile-bounce is a
 * paired-but-optional visual layer.
 */
export function resolveTileBounceForEvent(
  event: GameEvent,
  getTileMarker?: (nodeId: string) => THREE.Object3D | undefined,
): { poolId: 'tile_bounce'; args: TileVfxSpawnArgs } | null {
  if (event.type !== 'landing') return null;
  if (!getTileMarker) return null;

  const tileMarker = getTileMarker(event.nodeId);
  if (!tileMarker) return null;

  return {
    poolId: 'tile_bounce',
    args: {
      position: { x: 0, y: 0, z: 0 }, // not used by tile-bounce (animates tileMarker directly)
      tileMarker,
    },
  };
}
